import { ComplianceTemplate } from '@/firebase/complianceService';

// ============================================================================
// VERTEX AI MIGRATION - Enterprise Gemini AI for Bank Compliance
// ============================================================================
// Migrating from public Gemini API to Vertex AI for enterprise security
// All data is guaranteed NOT to be used for training (enterprise data governance)

// Flag to enable Vertex AI (set to true to use Vertex AI, false for legacy Gemini API)
const USE_VERTEX_AI = true;

// Get Firebase Functions URL for Vertex AI proxy
const getVertexAIProxyUrl = (): string => {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'lemur-476302';
  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.')
  );
  
  if (isLocalhost) {
    return `http://localhost:5001/${projectId}/us-central1/callVertexAI`;
  }
  
  return `https://us-central1-${projectId}.cloudfunctions.net/callVertexAI`;
};

/**
 * Call Vertex AI through Firebase Functions proxy
 * This ensures enterprise security and data governance
 */
async function callVertexAIProxy(
  prompt: string | string[],
  fileData?: { data: string; mimeType: string },
  returnJSON: boolean = false
): Promise<string | any> {
  try {
    const url = getVertexAIProxyUrl();
    console.log('[AI Search Service] [VertexAI Proxy] Calling:', {
      url,
      promptType: Array.isArray(prompt) ? 'array' : typeof prompt,
      promptLength: Array.isArray(prompt) ? prompt.length : (typeof prompt === 'string' ? prompt.length : 0),
      hasFileData: !!fileData,
      fileMimeType: fileData?.mimeType,
      returnJSON,
    });

    const requestBody = {
      operation: 'generateContent',
      prompt: Array.isArray(prompt) ? prompt : [prompt],
      fileData,
      returnJSON,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('[AI Search Service] [VertexAI Proxy] Response status:', response.status, response.statusText);

    // Get response text first to handle potential JSON parsing errors
    const responseText = await response.text();
    
    if (!response.ok) {
      let errorData: any = { error: `HTTP ${response.status}: ${response.statusText}` };
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        // If response is not JSON, use the text as error message
        console.error('[AI Search Service] [VertexAI Proxy] Non-JSON error response:', responseText.substring(0, 500));
        errorData = { error: responseText.substring(0, 500) || `HTTP ${response.status}: ${response.statusText}` };
      }
      console.error('[AI Search Service] [VertexAI Proxy] HTTP Error:', errorData);
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse JSON response
    let result: any;
    try {
      result = JSON.parse(responseText);
    } catch (parseError: any) {
      console.error('[AI Search Service] [VertexAI Proxy] JSON Parse Error:', parseError.message);
      console.error('[AI Search Service] [VertexAI Proxy] Response text (first 1000 chars):', responseText.substring(0, 1000));
      throw new Error(`Failed to parse JSON response: ${parseError.message}`);
    }

    console.log('[AI Search Service] [VertexAI Proxy] Response received:', {
      success: result.success,
      hasData: !!result.data,
      dataType: typeof result.data,
    });

    if (!result.success) {
      console.error('[AI Search Service] [VertexAI Proxy] API Error:', result.error);
      throw new Error(result.error || 'Vertex AI call failed');
    }

    return result.data;
  } catch (error: any) {
    console.error('[AI Search Service] [VertexAI Proxy] Error:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
    });
    throw new Error(`Vertex AI proxy error: ${error.message || 'Unknown error'}`);
  }
}

// Debug: Log initialization status
console.log('🤖 AI Search Service initialized:', {
  useVertexAI: USE_VERTEX_AI,
  proxyUrl: getVertexAIProxyUrl(),
});

export interface AISuggestion {
  reason: string;
  suggestedTemplates: {
    name: string;
    category: string;
    why: string;
  }[];
  alternativeTerms?: string[];
}

export interface NewTemplateSuggestion {
  name: string;
  category: string;
  frequency: string;
  owner_role: string;
  evidence_types: {
    type: string;
    label: string;
    required: boolean;
  }[];
  pass_fail_criteria: string;
  jurisdiction: string;
  jurisdiction_notes: string;
  risk_level: 'High' | 'Medium' | 'Low';
  lead_times_days: number[];
  description: string;
  why_relevant: string;
}

export interface NewAuditTemplateSuggestion {
  name: string;
  description?: string;
  auditType: string; // e.g., "SOC2", "ISO27001", "HIPAA", "PCI-DSS", "Custom"
  frequency: 'yearly' | 'semi-yearly' | 'quarterly';
  complianceTemplateNames: string[]; // Existing compliance templates to associate (by name)
  missingComplianceTemplates?: MissingComplianceTemplate[]; // Compliance templates that should exist but don't
  why_relevant: string;
  metadata?: {
    industry?: string;
    jurisdiction?: string;
    customAuditType?: string; // Temporary field for custom audit type input
    customAuditTypeName?: string; // Stored custom audit type name when auditType is "Custom"
  };
}

export interface MissingComplianceTemplate {
  name: string;
  category: string;
  frequency: string;
  owner_role: string;
  evidence_types: {
    type: string;
    label: string;
    required: boolean;
  }[];
  pass_fail_criteria: string;
  jurisdiction: string;
  jurisdiction_notes: string;
  risk_level: 'High' | 'Medium' | 'Low';
  lead_times_days: number[];
  description?: string;
  why_needed: string; // Why this compliance template is needed for this audit
}

export interface NewChecklistSuggestion {
  name: string;
  description: string;
  templateId: string;
  templateName: string;
  items: {
    text: string;
    order: number;
  }[];
  variables: string[];
  why_relevant: string;
}

export interface NewFormSuggestion {
  name: string;
  description: string;
  templateId: string;
  templateName: string;
  fields: {
    label: string;
    type: string;
    required?: boolean;
  }[];
  variables: string[];
  why_relevant: string;
}

export interface NewAssetTypeSuggestion {
  name: string;
  description: string;
  templateIds: string[];
  templateNames: string[];
  fields: {
    name: string;
    type: string;
    required?: boolean;
    description?: string;
  }[];
  why_relevant: string;
}

export interface NewAgreementSuggestion {
  name: string;
  description: string;
  templateId: string;
  templateName: string;
  suggestedSigners: {
    name: string;
    email?: string;
    role?: string;
  }[];
  documentContent?: string; // Suggested document content/template
  why_relevant: string;
}

export const aiSearchService = {
  /**
   * Get AI-powered template suggestions based on user query when no results are found
   */
  async getTemplateSuggestions(
    searchQuery: string,
    availableTemplates: ComplianceTemplate[]
  ): Promise<AISuggestion | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled, returning null');
      return null;
    }

    try {
      // Prepare template summary for AI context
      const templateSummary = availableTemplates.map(t => ({
        name: t.name,
        category: t.category,
        tags: [t.risk_level, t.frequency, t.owner_role, t.jurisdiction].filter(Boolean).join(', ')
      }));

      const prompt = `You are an AI assistant helping users find compliance templates. 

A user searched for: "${searchQuery}"

Available templates in the system:
${JSON.stringify(templateSummary, null, 2)}

The user's search did not return any results. Please provide:
1. A brief explanation of why their search didn't match
2. 3-5 suggested templates from the available templates that are most relevant to their search
3. Alternative search terms they could use

For each suggested template, explain why it might be relevant to their search.

Format your response as JSON:
{
  "reason": "brief explanation of why no results found",
  "suggestedTemplates": [
    {
      "name": "template name exactly as it appears in the list",
      "category": "template category",
      "why": "explanation of why this template is relevant"
    }
  ],
  "alternativeTerms": ["term1", "term2", "term3"]
}

Only suggest templates that actually exist in the available templates list. Be helpful and specific.`;

      console.log('[AI Search Service] ✅ Using Vertex AI for template suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate and filter suggested templates to only include those that actually exist
      const validatedSuggestions = {
        reason: parsed.reason || 'No exact matches found for your search.',
        suggestedTemplates: (parsed.suggestedTemplates || []).filter((suggestion: any) => 
          availableTemplates.some(t => t.name === suggestion.name)
        ).map((suggestion: any) => ({
          name: suggestion.name,
          category: suggestion.category,
          why: suggestion.why || 'This template might be relevant to your search.'
        })),
        alternativeTerms: parsed.alternativeTerms || []
      };

      return validatedSuggestions.suggestedTemplates.length > 0 ? validatedSuggestions : null;
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      return null;
    }
  },

  /**
   * Get default templates that can be imported
   */
  getDefaultTemplates(): Array<Omit<NewTemplateSuggestion, 'description' | 'why_relevant'>> {
    return [
      {
        "name": "Annual Fire Inspection",
        "category": "Life Safety",
        "frequency": "P1Y",
        "lead_times_days": [60, 30, 7],
        "owner_role": "Site Manager",
        "evidence_types": [
          { "type": "pdf", "label": "Fire Marshal Certificate", "required": true },
          { "type": "photo", "label": "Panel Photos", "required": false }
        ],
        "pass_fail_criteria": "All devices pass functional test; certificate received and filed.",
        "jurisdiction": "County/Fire Marshal",
        "jurisdiction_notes": "Salt Lake County Fire Marshal requires on-site posting of certificate for 12 months.",
        "risk_level": "High"
      },
      {
        "name": "Extinguisher Monthly Check",
        "category": "Life Safety",
        "frequency": "P1M",
        "lead_times_days": [7, 2],
        "owner_role": "Engineering Lead",
        "evidence_types": [{ "type": "photo", "label": "Tagged Units", "required": true }],
        "pass_fail_criteria": "All extinguishers accessible, tagged, gauge in green; log updated.",
        "jurisdiction": "OSHA/Fire Code",
        "jurisdiction_notes": "Maintain 12 months of logs; OSHA 29 CFR 1910.157.",
        "risk_level": "High"
      },
      {
        "name": "Emergency Lighting Test",
        "category": "Life Safety",
        "frequency": "P1M",
        "lead_times_days": [7, 2],
        "owner_role": "Engineering Lead",
        "evidence_types": [{ "type": "photo", "label": "Test Log Photos", "required": true }],
        "pass_fail_criteria": "All fixtures illuminate for test duration; defects corrected.",
        "jurisdiction": "NFPA/Local AHJ",
        "jurisdiction_notes": "NFPA 101 requires 90-minute annual test; schedule separately.",
        "risk_level": "High"
      },
      {
        "name": "Hood & Duct Cleaning",
        "category": "Life Safety",
        "frequency": "P6M",
        "lead_times_days": [30, 7],
        "owner_role": "Kitchen Manager",
        "evidence_types": [
          { "type": "pdf", "label": "Cleaning Certificate", "required": true },
          { "type": "photo", "label": "Before/After", "required": false }
        ],
        "pass_fail_criteria": "No grease accumulation; baffle filters reinstalled; certificate on file.",
        "jurisdiction": "Health Dept./Insurer",
        "jurisdiction_notes": "Frequency may increase with heavy/fry volume; verify with insurer/AHJ.",
        "risk_level": "High"
      },
      {
        "name": "Backflow Testing",
        "category": "Life Safety",
        "frequency": "P1Y",
        "lead_times_days": [45, 15, 7],
        "owner_role": "Maintenance Supervisor",
        "evidence_types": [{ "type": "pdf", "label": "Certified Test Report", "required": true }],
        "pass_fail_criteria": "Passed differential pressure test; report submitted if required.",
        "jurisdiction": "Water Authority",
        "jurisdiction_notes": "Annual certified tester report typically required by water purveyor.",
        "risk_level": "High"
      },
      {
        "name": "Sprinkler 5-Year Internal Inspection",
        "category": "Life Safety",
        "frequency": "P5Y",
        "lead_times_days": [90, 60, 30],
        "owner_role": "Engineering Lead",
        "evidence_types": [{ "type": "pdf", "label": "Internal Inspection Report", "required": true }],
        "pass_fail_criteria": "Pipes free of corrosion/obstruction per NFPA 25; deficiencies corrected.",
        "jurisdiction": "NFPA/Local AHJ",
        "jurisdiction_notes": "NFPA 25 standard item; confirm local adoption with AHJ.",
        "risk_level": "High"
      },
      {
        "name": "Elevator Permit Renewal",
        "category": "Building & Facilities",
        "frequency": "P1Y",
        "lead_times_days": [60, 30, 7],
        "owner_role": "Site Manager",
        "evidence_types": [{ "type": "pdf", "label": "Permit", "required": true }],
        "pass_fail_criteria": "Annual inspection completed; permit current and posted.",
        "jurisdiction": "State Elevator Program",
        "jurisdiction_notes": "Utah Labor Commission – Elevator Safety Division requirements.",
        "risk_level": "High"
      },
      {
        "name": "Boiler Inspection",
        "category": "Building & Facilities",
        "frequency": "P1Y",
        "lead_times_days": [45, 15, 7],
        "owner_role": "Engineering Lead",
        "evidence_types": [{ "type": "pdf", "label": "Inspection Report", "required": true }],
        "pass_fail_criteria": "Safety valves operational; no leaks; certificate or report on file.",
        "jurisdiction": "State/Insurer",
        "jurisdiction_notes": "State inspector or insurance carrier inspection may be required.",
        "risk_level": "High"
      },
      {
        "name": "ADA Door Operator Test",
        "category": "Building & Facilities",
        "frequency": "P3M",
        "lead_times_days": [14, 7],
        "owner_role": "Engineering Lead",
        "evidence_types": [
          { "type": "pdf", "label": "Maintenance Log", "required": true },
          { "type": "photo", "label": "Door Plate Labels", "required": false }
        ],
        "pass_fail_criteria": "Meets opening timing/force and sensor safety; labels present.",
        "jurisdiction": "ANSI/ADA",
        "jurisdiction_notes": "ANSI/BHMA A156.10/19; ensure safety stickers are visible.",
        "risk_level": "Medium"
      },
      {
        "name": "Emergency Generator Load Test",
        "category": "Building & Facilities",
        "frequency": "P1M",
        "lead_times_days": [7, 2],
        "owner_role": "Engineering Lead",
        "evidence_types": [{ "type": "pdf", "label": "Generator Log", "required": true }],
        "pass_fail_criteria": "Starts automatically and runs under load; log completed.",
        "jurisdiction": "NFPA 110",
        "jurisdiction_notes": "Maintain monthly exercise logs; service per manufacturer.",
        "risk_level": "High"
      },
      {
        "name": "Business License Renewal",
        "category": "Licensing & Administrative",
        "frequency": "P1Y",
        "lead_times_days": [60, 30, 7],
        "owner_role": "Site Manager",
        "evidence_types": [{ "type": "pdf", "label": "License", "required": true }],
        "pass_fail_criteria": "License renewed and posted; receipt on file.",
        "jurisdiction": "City",
        "jurisdiction_notes": "Renew via city portal; posting may be required.",
        "risk_level": "Medium"
      },
      {
        "name": "Health Department Permit",
        "category": "Licensing & Administrative",
        "frequency": "P1Y",
        "lead_times_days": [60, 30, 7],
        "owner_role": "Site Manager",
        "evidence_types": [
          { "type": "pdf", "label": "Permit", "required": true },
          { "type": "photo", "label": "Posting", "required": false }
        ],
        "pass_fail_criteria": "Permit active and visibly posted.",
        "jurisdiction": "County Health Dept.",
        "jurisdiction_notes": "Salt Lake County Health Department oversees most food permits.",
        "risk_level": "High"
      },
      {
        "name": "Liquor License Renewal",
        "category": "Licensing & Administrative",
        "frequency": "P1Y",
        "lead_times_days": [90, 60, 30],
        "owner_role": "Owner/Admin",
        "evidence_types": [{ "type": "pdf", "label": "License/Receipt", "required": true }],
        "pass_fail_criteria": "License renewed before expiration; documentation on file.",
        "jurisdiction": "State ABC",
        "jurisdiction_notes": "Utah DABS; start early to avoid lapse.",
        "risk_level": "High"
      },
      {
        "name": "Workers Comp Poster Update",
        "category": "Licensing & Administrative",
        "frequency": "P1Y",
        "lead_times_days": [30],
        "owner_role": "HR Manager",
        "evidence_types": [{ "type": "photo", "label": "Posted Poster", "required": true }],
        "pass_fail_criteria": "Current state poster displayed in common area.",
        "jurisdiction": "State Labor",
        "jurisdiction_notes": "Utah Labor Commission posting required; update when content changes.",
        "risk_level": "Medium"
      },
      {
        "name": "Occupational Safety Posters",
        "category": "Licensing & Administrative",
        "frequency": "P1Y",
        "lead_times_days": [30],
        "owner_role": "HR Manager",
        "evidence_types": [{ "type": "photo", "label": "Posting Area", "required": true }],
        "pass_fail_criteria": "OSHA-required posters up-to-date and visible.",
        "jurisdiction": "Federal/OSHA",
        "jurisdiction_notes": "OSHA 3165; some posters must be updated when rules change.",
        "risk_level": "Medium"
      },
      {
        "name": "Grease Trap Cleaning",
        "category": "Environmental & Utilities",
        "frequency": "P3M",
        "lead_times_days": [14, 7],
        "owner_role": "Kitchen Manager",
        "evidence_types": [
          { "type": "pdf", "label": "Service Ticket", "required": true },
          { "type": "photo", "label": "Cleanout", "required": false }
        ],
        "pass_fail_criteria": "Trap pumped; disposal manifest recorded; no backups.",
        "jurisdiction": "County Health",
        "jurisdiction_notes": "County may require manifests; keep records for inspection.",
        "risk_level": "High"
      },
      {
        "name": "Waste Hauler Permit",
        "category": "Environmental & Utilities",
        "frequency": "P1Y",
        "lead_times_days": [60, 30, 7],
        "owner_role": "Site Manager",
        "evidence_types": [{ "type": "pdf", "label": "Permit", "required": true }],
        "pass_fail_criteria": "Permit active; vendor compliant with pickups.",
        "jurisdiction": "City/County",
        "jurisdiction_notes": "Renew through local waste authority; keep copy on file.",
        "risk_level": "Medium"
      },
      {
        "name": "Recycling Audit",
        "category": "Environmental & Utilities",
        "frequency": "P6M",
        "lead_times_days": [30],
        "owner_role": "Facilities Manager",
        "evidence_types": [{ "type": "photo", "label": "Bins & Signage", "required": false }],
        "pass_fail_criteria": "Correct bins present; signage readable; contamination minimized.",
        "jurisdiction": "Corporate/Voluntary",
        "jurisdiction_notes": "Often part of sustainability programs; not always mandated.",
        "risk_level": "Low"
      },
      {
        "name": "Workers Comp Audit",
        "category": "Insurance & Finance",
        "frequency": "P1Y",
        "lead_times_days": [45, 15, 7],
        "owner_role": "Owner/Admin",
        "evidence_types": [
          { "type": "pdf", "label": "Audit Letter", "required": true },
          { "type": "pdf", "label": "Proof of Payment", "required": true }
        ],
        "pass_fail_criteria": "Audit completed; variances reconciled; payment posted.",
        "jurisdiction": "Insurer/State",
        "jurisdiction_notes": "Retain records for 3–5 years for audits.",
        "risk_level": "Medium"
      },
      {
        "name": "CPR / First Aid Certification",
        "category": "Training & HR",
        "frequency": "P2Y",
        "lead_times_days": [60, 30],
        "owner_role": "HR Manager",
        "evidence_types": [{ "type": "pdf", "label": "Certificates", "required": true }],
        "pass_fail_criteria": "At least one staff member per shift certified.",
        "jurisdiction": "Best Practice/Insurer",
        "jurisdiction_notes": "Often required by insurer or brand standards.",
        "risk_level": "Medium"
      },
      {
        "name": "Harassment Prevention Training",
        "category": "Training & HR",
        "frequency": "P1Y",
        "lead_times_days": [30],
        "owner_role": "HR Manager",
        "evidence_types": [{ "type": "pdf", "label": "Completion Log", "required": true }],
        "pass_fail_criteria": "100% of staff completed by deadline.",
        "jurisdiction": "State/Company Policy",
        "jurisdiction_notes": "Utah mandates annual training for many employers; confirm scope.",
        "risk_level": "Medium"
      },
      {
        "name": "Food Handler Permit",
        "category": "Training & HR",
        "frequency": "P3Y",
        "lead_times_days": [90, 30, 7],
        "owner_role": "Kitchen Manager",
        "evidence_types": [{ "type": "pdf", "label": "Cards", "required": true }],
        "pass_fail_criteria": "All food handlers hold valid, non-expired permits.",
        "jurisdiction": "County Health Dept.",
        "jurisdiction_notes": "Salt Lake County Health Dept. issues/validates permits.",
        "risk_level": "High"
      }
    ];
  },

  /**
   * Search default templates by query
   */
  searchDefaultTemplates(searchQuery: string, existingTemplateNames: string[]): NewTemplateSuggestion[] {
    const defaultTemplates = this.getDefaultTemplates();
    const queryLower = searchQuery.toLowerCase();
    
    // Filter default templates that match the query and don't already exist
    const matchingTemplates = defaultTemplates.filter(template => {
      const isExisting = existingTemplateNames.some(name => 
        name.toLowerCase() === template.name.toLowerCase()
      );
      
      if (isExisting) return false;
      
      return (
        template.name.toLowerCase().includes(queryLower) ||
        template.category.toLowerCase().includes(queryLower) ||
        template.jurisdiction.toLowerCase().includes(queryLower) ||
        template.pass_fail_criteria.toLowerCase().includes(queryLower)
      );
    });
    
    // Convert to NewTemplateSuggestion format
    return matchingTemplates.map(template => ({
      ...template,
      description: `${template.category} compliance requirement`,
      why_relevant: `Matches your search for "${searchQuery}" - ${template.jurisdiction} requirement`
    } as NewTemplateSuggestion));
  },

  /**
   * Generate new compliance template suggestions based on external knowledge
   * This creates suggestions for templates that DON'T exist in the system yet
   */
  async generateNewTemplateSuggestions(
    searchQuery: string,
    existingCategories: string[],
    existingTemplateNames: string[]
  ): Promise<NewTemplateSuggestion[] | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled for new template suggestions');
      return null;
    }
    
    console.log('🔍 Generating new template suggestions...');

    try {
      const prompt = `You are an expert in compliance and regulatory requirements. A user is searching for compliance templates related to: "${searchQuery}"

IMPORTANT: The following templates ALREADY EXIST in the system. DO NOT suggest these or variations of these:
${existingTemplateNames.map(name => `- ${name}`).join('\n')}

Based on your knowledge of compliance requirements (food safety, health codes, OSHA, fire codes, environmental regulations, business licenses, ADA requirements, etc.), suggest 2-3 COMPLETELY NEW and DIFFERENT compliance templates that would be relevant to this search. 

These must be DIFFERENT compliance items - not variations of existing templates. Focus on other aspects or requirements related to the search query.

For each template, provide comprehensive details including:
- A clear, descriptive name
- Appropriate category (use existing categories if similar, or suggest a new one)
- Typical renewal frequency (P1Y for annual, P6M for semi-annual, P3M for quarterly, P1M for monthly, P2Y for biennial, P5Y for 5-year)
- Owner role (Site Manager, Engineering Lead, Kitchen Manager, Maintenance Supervisor, Owner/Admin, HR Manager, Facilities Manager)
- Evidence types needed (certificate, pdf, photo, log, report, inspection, manifest)
- Pass/fail criteria description
- Jurisdiction (government agency or organization that requires this)
- Jurisdiction notes (any additional context)
- Risk level (High, Medium, or Low)
- Typical lead time in days before due date when alerts should be sent (usually [30, 60, 90])

Existing categories in the system: ${existingCategories.join(', ')}

Provide your response as JSON array:
[
  {
    "name": "specific template name",
    "category": "category name",
    "frequency": "P1Y",
    "owner_role": "Site Manager",
    "evidence_types": [
      {"type": "certificate", "label": "License Certificate", "required": true},
      {"type": "pdf", "label": "Application Form", "required": false}
    ],
    "pass_fail_criteria": "Clear description of what constitutes pass/fail",
    "jurisdiction": "Agency or organization name",
    "jurisdiction_notes": "Additional context about requirements",
    "risk_level": "High",
    "lead_times_days": [30, 60, 90],
    "description": "Brief description of what this compliance item is for",
    "why_relevant": "Explanation of why this matches the user's search"
  }
]

Focus on real, actual compliance requirements. Be specific and accurate.`;

      console.log('[AI Search Service] ✅ Using Vertex AI for new template suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate the structure and filter out duplicates
      if (Array.isArray(parsed)) {
        const suggestions = parsed.map((item: any) => ({
          name: item.name || 'Untitled Template',
          category: item.category || 'General',
          frequency: item.frequency || 'P1Y',
          owner_role: item.owner_role || 'Site Manager',
          evidence_types: Array.isArray(item.evidence_types) ? item.evidence_types : [],
          pass_fail_criteria: item.pass_fail_criteria || '',
          jurisdiction: item.jurisdiction || '',
          jurisdiction_notes: item.jurisdiction_notes || '',
          risk_level: item.risk_level || 'Medium',
          lead_times_days: Array.isArray(item.lead_times_days) ? item.lead_times_days : [30, 60, 90],
          description: item.description || '',
          why_relevant: item.why_relevant || 'Relevant to your search criteria'
        })) as NewTemplateSuggestion[];

        // Filter out suggestions that match existing template names (case-insensitive, partial matching)
        const filtered = suggestions.filter(suggestion => {
          const suggestionNameLower = suggestion.name.toLowerCase();
          return !existingTemplateNames.some(existingName => {
            const existingNameLower = existingName.toLowerCase();
            // Check for exact match or if suggestion name contains existing name or vice versa
            return suggestionNameLower === existingNameLower ||
                   suggestionNameLower.includes(existingNameLower) ||
                   existingNameLower.includes(suggestionNameLower);
          });
        });

        return filtered.length > 0 ? filtered : null;
      }

      return null;
    } catch (error) {
      console.error('Error generating new template suggestions:', error);
      return null;
    }
  },

  /**
   * Simple fallback: Find templates by fuzzy matching keywords
   */
  getFallbackSuggestions(
    searchQuery: string,
    availableTemplates: ComplianceTemplate[]
  ): AISuggestion | null {
    const queryLower = searchQuery.toLowerCase();
    const keywords = queryLower.split(/\s+/).filter(word => word.length > 2);

    // Score templates based on keyword matches
    const scoredTemplates = availableTemplates.map(template => {
      let score = 0;
      const templateText = `${template.name} ${template.category} ${template.owner_role} ${template.jurisdiction}`.toLowerCase();
      
      keywords.forEach(keyword => {
        if (templateText.includes(keyword)) {
          score += 1;
        }
      });

      return { template, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

    if (scoredTemplates.length === 0) {
      return null;
    }

    return {
      reason: `No exact matches found for "${searchQuery}". Here are some related templates:`,
      suggestedTemplates: scoredTemplates.map(item => ({
        name: item.template.name,
        category: item.template.category,
        why: `Matches your search terms: ${keywords.join(', ')}`
      })),
      alternativeTerms: []
    };
  },

  /**
   * Stricter fallback: Only show templates that match multiple keywords or are truly relevant
   * This prevents showing templates that only match on one word
   */
  getStrictFallbackSuggestions(
    searchQuery: string,
    availableTemplates: ComplianceTemplate[]
  ): AISuggestion | null {
    const queryLower = searchQuery.toLowerCase().trim();
    const keywords = queryLower.split(/\s+/).filter(word => word.length > 2);
    
    // For searches like "ADA Parking", require ALL keywords to match meaningfully
    // This prevents "ADA Door Operator Test" from matching when searching for "ADA Parking"
    
    // If the query has multiple words, require ALL of them to be present
    if (keywords.length >= 2) {
      const scoredTemplates = availableTemplates.map(template => {
        const templateNameLower = template.name.toLowerCase();
        const templateText = `${template.name} ${template.category}`.toLowerCase();
        
        // Check if ALL keywords are present in the template name or category
        const allKeywordsMatch = keywords.every(keyword => {
          // Check if keyword appears as a whole word in the template name
          const wordPattern = new RegExp(`\\b${keyword}\\b`, 'i');
          return wordPattern.test(templateText);
        });
        
        // Only include if ALL keywords match
        if (allKeywordsMatch) {
          // Score based on how closely it matches
          let score = 0;
          if (templateNameLower.includes(queryLower)) {
            score = 10; // Exact phrase match
          } else {
            score = keywords.length * 2; // All keywords match
          }
          
          return { template, score, matchingKeywords: keywords.length };
        }
        
        return null;
      })
      .filter((item): item is { template: ComplianceTemplate; score: number; matchingKeywords: number } => item !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

      if (scoredTemplates.length === 0) {
        return null; // No good matches - don't show misleading suggestions
      }

      return {
        reason: `Found some existing templates that match "${searchQuery}":`,
        suggestedTemplates: scoredTemplates.map(item => ({
          name: item.template.name,
          category: item.template.category,
          why: 'Matches all your search keywords'
        })),
        alternativeTerms: []
      };
    }
    
    // For single keyword searches, use stricter matching
    if (keywords.length === 1 && searchQuery.length >= 5) {
      const keyword = keywords[0];
      const wordPattern = new RegExp(`\\b${keyword}\\b`, 'i');
      
      const matches = availableTemplates
        .filter(template => {
          const templateText = `${template.name} ${template.category}`.toLowerCase();
          return wordPattern.test(templateText);
        })
        .slice(0, 3);

      if (matches.length === 0) {
        return null;
      }

      return {
        reason: `Found existing templates containing "${keyword}":`,
        suggestedTemplates: matches.map(template => ({
          name: template.name,
          category: template.category,
          why: `Contains "${keyword}"`
        })),
        alternativeTerms: []
      };
    }

    // Don't show suggestions for very short or unclear queries
    return null;
  },

  /**
   * Generate new form suggestions based on external knowledge
   * This creates suggestions for forms that DON'T exist in the system yet
   */
  async generateNewFormSuggestions(
    searchQuery: string,
    existingFormNames: string[],
    availableTemplates: ComplianceTemplate[]
  ): Promise<NewFormSuggestion[] | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled for new form suggestions');
      return null;
    }
    
    console.log('🔍 Generating new form suggestions...');

    try {

      // Prepare template summary for context
      const templateSummary = availableTemplates.map(t => ({
        id: t.id,
        name: t.name,
        category: t.category
      }));

      const prompt = `You are helping a facility manager add a NEW FORM to their data collection system.

SEARCH QUERY: "${searchQuery}"

YOUR TASK: Generate 2-3 suggestions for DATA COLLECTION FORMS (input forms with fields) that match this search.

KEY RULE: Suggest FORMS with input fields for data entry - NOT compliance templates or procedures.

FORM EXAMPLES (what you should suggest):
• Incident Report Form
• Employee Training Sign-Off Form
• Vendor Delivery Form
• Maintenance Request Form
• Equipment Audit Form
• Safety Inspection Form
• Customer Complaint Form
• Inventory Count Form

NOT FORMS (these are compliance items - DO NOT suggest):
❌ Annual Fire Inspection (this is a compliance template)
❌ Permit Renewal (this is a compliance task)
❌ Certification Process (this is a compliance requirement)

Existing forms (don't suggest these):
${existingFormNames.length > 0 ? existingFormNames.map(name => `• ${name}`).join('\n') : '• (none yet)'}

Available compliance templates (link forms to these if relevant):
${JSON.stringify(templateSummary, null, 2)}

For each form, return this JSON structure:
  {
  "name": "Form Name",
  "description": "What this form is used for",
  "templateId": "id1" or "",
  "templateName": "Name1" or "",
    "fields": [
    {"label": "Field Name", "type": "text", "required": true},
    {"label": "Date", "type": "date", "required": true}
    ],
  "variables": ["Variable1", "Variable2"] or [],
  "why_relevant": "Why this matches the search"
  }

EXAMPLE for "incident report":
{
  "name": "Incident Report Form",
  "description": "Form for documenting workplace incidents and accidents",
  "templateId": "",
  "templateName": "",
  "fields": [
    {"label": "Incident Date", "type": "date", "required": true},
    {"label": "Time of Incident", "type": "text", "required": true},
    {"label": "Location", "type": "text", "required": true},
    {"label": "Personnel Involved", "type": "textarea", "required": false},
    {"label": "Description", "type": "textarea", "required": true},
    {"label": "Witness Name", "type": "text", "required": false},
    {"label": "Reported By", "type": "text", "required": true},
    {"label": "Action Taken", "type": "textarea", "required": false}
  ],
  "variables": ["Location", "Reported By"],
  "why_relevant": "Incident forms are essential for safety documentation and compliance"
}

Return your response as a JSON array with 2-3 form suggestions. Field types: text, number, date, email, tel, textarea, select, checkbox, radio`;

      console.log('[AI Search Service] ✅ Using Vertex AI for new form suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate the structure and filter out duplicates
      if (Array.isArray(parsed)) {
        const suggestions = parsed.map((item: any) => {
          // Validate template exists
          const matchingTemplate = availableTemplates.find(t => 
            t.id === item.templateId || t.name === item.templateName
          );
          
          if (!matchingTemplate) {
            const fallbackTemplate = availableTemplates.find(t => 
              t.category.toLowerCase().includes(item.category?.toLowerCase() || '') ||
              t.name.toLowerCase().includes(item.templateName?.toLowerCase() || '')
            );
            
            if (!fallbackTemplate) {
              const anyTemplate = availableTemplates[0];
              if (!anyTemplate) return null;
              
              return {
                name: item.name || 'Untitled Form',
                description: item.description || '',
                templateId: anyTemplate.id,
                templateName: anyTemplate.name,
                fields: Array.isArray(item.fields) ? item.fields.map((field: any, idx: number) => ({
                  label: field.label || `Field ${idx + 1}`,
                  type: field.type || 'text',
                  required: field.required || false,
                  order: idx + 1
                })) : [],
                variables: Array.isArray(item.variables) ? item.variables : [],
                why_relevant: item.why_relevant || 'Relevant to your search criteria'
              };
            }
            
            return {
              name: item.name || 'Untitled Form',
              description: item.description || '',
              templateId: fallbackTemplate.id,
              templateName: fallbackTemplate.name,
              fields: Array.isArray(item.fields) ? item.fields.map((field: any, idx: number) => ({
                label: field.label || `Field ${idx + 1}`,
                type: field.type || 'text',
                required: field.required || false,
                order: idx + 1
              })) : [],
              variables: Array.isArray(item.variables) ? item.variables : [],
              why_relevant: item.why_relevant || 'Relevant to your search criteria'
            };
          }
          
          return {
            name: item.name || 'Untitled Form',
            description: item.description || '',
            templateId: matchingTemplate.id,
            templateName: matchingTemplate.name,
            fields: Array.isArray(item.fields) ? item.fields.map((field: any, idx: number) => ({
              label: field.label || `Field ${idx + 1}`,
              type: field.type || 'text',
              required: field.required || false,
              order: idx + 1
            })) : [],
            variables: Array.isArray(item.variables) ? item.variables : [],
            why_relevant: item.why_relevant || 'Relevant to your search criteria'
          };
        }).filter((item): item is NewFormSuggestion => item !== null);

        // Filter out suggestions that match existing form names
        const filtered = suggestions.filter(suggestion => {
          const suggestionNameLower = suggestion.name.toLowerCase();
          return !existingFormNames.some(existingName => {
            const existingNameLower = existingName.toLowerCase();
            return suggestionNameLower === existingNameLower ||
                   suggestionNameLower.includes(existingNameLower) ||
                   existingNameLower.includes(suggestionNameLower);
          });
        });

        return filtered.length > 0 ? filtered : null;
      }

      return null;
    } catch (error: any) {
      console.error('Error generating form suggestions:', error);
      return null;
    }
  },

  /**
   * Generate new checklist suggestions based on external knowledge
   * This creates suggestions for checklists that DON'T exist in the system yet
   */
  async generateNewChecklistSuggestions(
    searchQuery: string,
    existingChecklistNames: string[],
    availableTemplates: ComplianceTemplate[]
  ): Promise<NewChecklistSuggestion[] | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled for new checklist suggestions');
      return null;
    }
    
    console.log('🔍 Generating new checklist suggestions...');

    try {

      // Prepare template summary for context
      const templateSummary = availableTemplates.map(t => ({
        id: t.id,
        name: t.name,
        category: t.category
      }));

      const prompt = `You are helping a facility manager add a NEW CHECKLIST to their inspection system.

SEARCH QUERY: "${searchQuery}"

YOUR TASK: Generate 2-3 suggestions for CHECKLISTS (inspection checklists with line items) that match this search.

KEY RULE: Suggest CHECKLISTS with actionable inspection items - NOT compliance templates or procedures.

CHECKLIST EXAMPLES (what you should suggest):
• Daily Equipment Inspection Checklist
• Weekly Safety Walk-Through Checklist
• Monthly Preventive Maintenance Checklist
• Fire Safety Equipment Checklist
• Emergency Preparedness Checklist
• Cleanliness Audit Checklist
• Vendor Delivery Checklist
• Employee Training Completion Checklist

NOT CHECKLISTS (these are compliance items - DO NOT suggest):
❌ Annual Fire Inspection (this is a compliance template)
❌ Permit Renewal (this is a compliance task)
❌ Certification Process (this is a compliance requirement)

Existing checklists (don't suggest these):
${existingChecklistNames.length > 0 ? existingChecklistNames.map(name => `• ${name}`).join('\n') : '• (none yet)'}

Available compliance templates (link checklists to these if relevant):
${JSON.stringify(templateSummary, null, 2)}

For each checklist, return this JSON structure:
{
  "name": "Checklist Name",
  "description": "What this checklist is used for",
  "templateId": "id1" or "",
  "templateName": "Name1" or "",
  "items": ["Item 1", "Item 2", "Item 3"],
  "variables": ["Variable1", "Variable2"] or [],
  "why_relevant": "Why this matches the search"
}

EXAMPLE for "fire safety":
  {
  "name": "Fire Safety Equipment Inspection",
  "description": "Monthly inspection checklist for fire safety equipment verification",
  "templateId": "",
  "templateName": "",
    "items": [
    "Verify fire extinguisher pressure gauge in green zone",
    "Check fire alarm panel has no fault indicators",
    "Test emergency exit lighting functionality",
    "Inspect sprinkler heads for damage or obstructions",
    "Verify fire doors close automatically and completely",
    "Check fire escape routes are clear and accessible"
    ],
  "variables": ["Location", "Inspector Name"],
  "why_relevant": "Fire safety checklists ensure emergency equipment is operational"
}

Return your response as a JSON array with 2-3 checklist suggestions.`;

      console.log('[AI Search Service] ✅ Using Vertex AI for new checklist suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate the structure and filter out duplicates
      if (Array.isArray(parsed)) {
        const suggestions = parsed.map((item: any) => {
          // Validate template exists
          const matchingTemplate = availableTemplates.find(t => 
            t.id === item.templateId || t.name === item.templateName
          );
          
          if (!matchingTemplate) {
            // Skip if template doesn't exist, but try to find closest match
            const fallbackTemplate = availableTemplates.find(t => 
              t.category.toLowerCase().includes(item.category?.toLowerCase() || '') ||
              t.name.toLowerCase().includes(item.templateName?.toLowerCase() || '')
            );
            
            if (!fallbackTemplate) {
              // Still return the suggestion even without a matching template
              // The user can manually link to a template later
              const anyTemplate = availableTemplates[0]; // Use first available template as fallback
              if (!anyTemplate) return null;
              
              return {
                name: item.name || 'Untitled Checklist',
                description: item.description || '',
                templateId: anyTemplate.id,
                templateName: anyTemplate.name,
                items: Array.isArray(item.items) ? item.items.map((text: string, idx: number) => ({
                  text: text,
                  order: idx + 1
                })) : [],
                variables: Array.isArray(item.variables) ? item.variables : [],
                why_relevant: item.why_relevant || 'Relevant to your search criteria'
              };
            }
            
            return {
              name: item.name || 'Untitled Checklist',
              description: item.description || '',
              templateId: fallbackTemplate.id,
              templateName: fallbackTemplate.name,
              items: Array.isArray(item.items) ? item.items.map((text: string, idx: number) => ({
                text: text,
                order: idx + 1
              })) : [],
              variables: Array.isArray(item.variables) ? item.variables : [],
              why_relevant: item.why_relevant || 'Relevant to your search criteria'
            };
          }
          
          return {
            name: item.name || 'Untitled Checklist',
            description: item.description || '',
            templateId: matchingTemplate.id,
            templateName: matchingTemplate.name,
            items: Array.isArray(item.items) ? item.items.map((text: string, idx: number) => ({
              text: text,
              order: idx + 1
            })) : [],
            variables: Array.isArray(item.variables) ? item.variables : [],
            why_relevant: item.why_relevant || 'Relevant to your search criteria'
          };
        }).filter((item): item is NewChecklistSuggestion => item !== null);

        // Filter out suggestions that match existing checklist names
        const filtered = suggestions.filter(suggestion => {
          const suggestionNameLower = suggestion.name.toLowerCase();
          return !existingChecklistNames.some(existingName => {
            const existingNameLower = existingName.toLowerCase();
            return suggestionNameLower === existingNameLower ||
                   suggestionNameLower.includes(existingNameLower) ||
                   existingNameLower.includes(suggestionNameLower);
          });
        });

        return filtered.length > 0 ? filtered : null;
      }

      return null;
    } catch (error) {
      console.error('Error generating new checklist suggestions:', error);
      return null;
    }
  },

  /**
   * Generate new asset type suggestions based on external knowledge
   * This creates suggestions for asset types that DON'T exist in the system yet
   */
  async generateNewAssetTypeSuggestions(
    searchQuery: string,
    existingAssetTypeNames: string[],
    availableTemplates: ComplianceTemplate[]
  ): Promise<NewAssetTypeSuggestion[] | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled for new asset type suggestions');
      return null;
    }
    
    console.log('🔍 Generating new asset type suggestions...');

    try {

      // Prepare template summary for context
      const templateSummary = availableTemplates.map(t => ({
        id: t.id,
        name: t.name,
        category: t.category
      }));

      const prompt = `You are a facilities management expert helping add asset types to an equipment tracking system.

SEARCH QUERY: "${searchQuery}"

YOUR TASK: Generate 2-3 suggestions for PHYSICAL EQUIPMENT/ASSETS that match this search.

KEY RULE: Suggest only TANGIBLE, PHYSICAL ITEMS that can be installed, inventoried, and tracked with serial numbers.

GOOD EXAMPLES (physical assets):
• Commercial Washing Machine
• Commercial Dryer  
• Ice Machine
• Fire Extinguisher
• Elevator
• Backup Generator
• Boiler
• Commercial Refrigerator
• Commercial Dishwasher
• HVAC Unit
• Commercial Coffee Machine
• Trash Compactor
• Ventilation Hood
• Water Heater

BAD EXAMPLES (not assets - these are tasks/processes):
❌ Fire Inspection
❌ Filter Change  
❌ Maintenance Schedule
❌ HVAC Inspection
❌ Grease Trap Cleaning
❌ Boiler Inspection

Existing assets (don't suggest these):
${existingAssetTypeNames.length > 0 ? existingAssetTypeNames.map(name => `• ${name}`).join('\n') : '• (none yet)'}

Available compliance templates (link assets to these if relevant):
${JSON.stringify(templateSummary, null, 2)}

For each asset, return this JSON structure:
{
  "name": "Asset Name",
  "description": "What this equipment is",
  "templateIds": ["id1", "id2"] or [],
  "templateNames": ["Name1", "Name2"] or [],
  "fields": [
    {"name": "Serial Number", "type": "text", "required": true, "description": "Unique identifier"},
    {"name": "Manufacturer", "type": "text", "required": false, "description": "Equipment manufacturer"},
    {"name": "Model", "type": "text", "required": false, "description": "Model number"},
    {"name": "Installation Date", "type": "date", "required": false, "description": "Installation date"},
    {"name": "Warranty End", "type": "date", "required": false, "description": "Warranty expiration"}
  ],
  "why_relevant": "Why this matches the search"
}

EXAMPLE for "commercial washer":
{
  "name": "Commercial Washer",
  "description": "Large capacity industrial washing machine for commercial laundry facilities",
  "templateIds": [],
  "templateNames": [],
  "fields": [
    {"name": "Serial Number", "type": "text", "required": true, "description": "Unique identifier"},
    {"name": "Manufacturer", "type": "text", "required": false, "description": "Brand name"},
    {"name": "Model Number", "type": "text", "required": false, "description": "Equipment model"},
    {"name": "Purchase Date", "type": "date", "required": false, "description": "Purchase date"},
    {"name": "Warranty Expiration", "type": "date", "required": false, "description": "Warranty end date"},
    {"name": "Capacity (lbs)", "type": "number", "required": false, "description": "Maximum load weight"},
    {"name": "Service Provider", "type": "text", "required": false, "description": "Maintenance company"}
  ],
  "why_relevant": "Commercial washers are standard equipment in hotel laundry facilities"
}

Return your response as a JSON array with 2-3 suggestions. Use field types: text, number, date, email, tel, textarea`;

      console.log('[AI Search Service] ✅ Using Vertex AI for new asset type suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate the structure and filter out duplicates
      if (Array.isArray(parsed)) {
        const suggestions = parsed.map((item: any) => {
          // Validate templates exist
          const allMatchingTemplates: ComplianceTemplate[] = [];
          
          // Try matching by IDs
          (item.templateIds || []).forEach((tid: string) => {
            const template = availableTemplates.find((t: ComplianceTemplate) => t.id === tid || t.name === tid);
            if (template) allMatchingTemplates.push(template);
          });

          // If no matches by ID, try matching by names
          if (allMatchingTemplates.length === 0) {
            (item.templateNames || []).forEach((tname: string) => {
              const template = availableTemplates.find((t: ComplianceTemplate) => t.name === tname);
              if (template) allMatchingTemplates.push(template);
            });
          }

          // If still no matches, use first available template
          const finalTemplates = allMatchingTemplates.length > 0 
            ? allMatchingTemplates 
            : availableTemplates.slice(0, 1);

          if (finalTemplates.length === 0) return null;
          
          return {
            name: item.name || 'Untitled Asset Type',
            description: item.description || '',
            templateIds: finalTemplates.map((t: ComplianceTemplate) => t.id!),
            templateNames: finalTemplates.map((t: ComplianceTemplate) => t.name),
            fields: Array.isArray(item.fields) ? item.fields.map((field: any, idx: number) => ({
              name: field.name || `Field ${idx + 1}`,
              type: field.type || 'text',
              required: field.required || false,
              description: field.description || ''
            })) : [],
            why_relevant: item.why_relevant || 'Relevant to your search criteria'
          };
        }).filter((item): item is NewAssetTypeSuggestion => item !== null);

        // Filter out suggestions that match existing asset type names
        const filtered = suggestions.filter(suggestion => {
          const suggestionNameLower = suggestion.name.toLowerCase();
          return !existingAssetTypeNames.some(existingName => {
            const existingNameLower = existingName.toLowerCase();
            return suggestionNameLower === existingNameLower ||
                   suggestionNameLower.includes(existingNameLower) ||
                   existingNameLower.includes(suggestionNameLower);
          });
        });

        return filtered.length > 0 ? filtered : null;
      }

      return null;
    } catch (error) {
      console.error('Error generating new asset type suggestions:', error);
      return null;
    }
  },

  /**
   * Generate new agreement suggestions based on external knowledge
   * This creates suggestions for agreements/contracts that DON'T exist in the system yet
   */
  async generateNewAgreementSuggestions(
    searchQuery: string,
    existingAgreementNames: string[],
    availableTemplates: ComplianceTemplate[]
  ): Promise<NewAgreementSuggestion[] | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled for new agreement suggestions');
      return null;
    }
    
    console.log('🔍 Generating new agreement suggestions...');

    try {

      // Prepare template summary for context
      const templateSummary = availableTemplates.map(t => ({
        id: t.id,
        name: t.name,
        category: t.category
      }));

      const prompt = `You are helping a facility manager add a NEW AGREEMENT/CONTRACT to their system.

SEARCH QUERY: "${searchQuery}"

YOUR TASK: Generate 2-3 suggestions for AGREEMENTS/CONTRACTS that match this search.

KEY RULE: Suggest LEGAL AGREEMENTS/CONTRACTS that require signatures - NOT compliance templates or procedures.

AGREEMENT EXAMPLES (what you should suggest):
• Vendor Service Agreement
• Employee NDA (Non-Disclosure Agreement)
• Equipment Lease Agreement
• Maintenance Contract
• Insurance Policy Agreement
• Subcontractor Agreement
• License Agreement
• Safety Acknowledgment Form

NOT AGREEMENTS (these are compliance items - DO NOT suggest):
❌ Annual Fire Inspection (this is a compliance template)
❌ Permit Renewal (this is a compliance task)
❌ Insurance Policy Renewal (this is a compliance event)

Existing agreements (don't suggest these):
${existingAgreementNames.length > 0 ? existingAgreementNames.map(name => `• ${name}`).join('\n') : '• (none yet)'}

Available compliance templates (link agreements to these if relevant):
${JSON.stringify(templateSummary, null, 2)}

For each agreement, return this JSON structure:
{
  "name": "Agreement Name",
  "description": "What this agreement is for",
  "templateId": "id1" or "",
  "templateName": "Name1" or "",
  "suggestedSigners": [
    {"name": "Signer Name", "role": "Owner/Manager"},
    {"name": "Another Signer", "role": "Contractor"}
  ],
  "documentContent": "Suggested contract text/content that can be used to create the actual document",
  "why_relevant": "Why this matches the search"
}

EXAMPLE for "vendor service":
{
  "name": "Vendor Service Agreement",
  "description": "Standard service agreement for contracted vendors and service providers",
  "templateId": "",
  "templateName": "",
  "suggestedSigners": [
    {"name": "Facility Manager", "role": "Contract Holder"},
    {"name": "Vendor Representative", "role": "Service Provider"}
  ],
  "documentContent": "VENDOR SERVICE AGREEMENT\n\nThis Agreement is entered into on [DATE] between [FACILITY NAME] (\"Client\") and [VENDOR NAME] (\"Vendor\").\n\n1. SERVICES: Vendor agrees to provide the following services: [SERVICE DESCRIPTION]\n\n2. TERM: This agreement shall commence on [START DATE] and continue until [END DATE], unless terminated earlier.\n\n3. COMPENSATION: Client agrees to pay Vendor $[AMOUNT] per [PAYMENT FREQUENCY].\n\n4. TERMINATION: Either party may terminate this agreement with [NOTICE PERIOD] days written notice.\n\n5. CONFIDENTIALITY: Vendor agrees to keep all client information confidential.\n\nIN WITNESS WHEREOF, the parties have executed this Agreement.\n\n[SIGNATURES]",
  "why_relevant": "Vendor agreements are essential for formalizing service relationships"
}

IMPORTANT: Include a complete, professional documentContent field with placeholder bracketed text like [DATE], [FACILITY NAME], [VENDOR NAME] that can be easily replaced by the user.

Return your response as a JSON array with 2-3 agreement suggestions.`;

      console.log('[AI Search Service] ✅ Using Vertex AI for new agreement suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate the structure and filter out duplicates
      if (Array.isArray(parsed)) {
        const suggestions = parsed.map((item: any) => {
          // Validate template exists
          const matchingTemplate = availableTemplates.find((t: ComplianceTemplate) => 
            t.id === item.templateId || t.name === item.templateName
          );
          
          const finalTemplate = matchingTemplate || availableTemplates[0];
          
          if (!finalTemplate) return null;
          
          const suggestion: NewAgreementSuggestion = {
            name: item.name || 'Untitled Agreement',
            description: item.description || '',
            templateId: finalTemplate.id!,
            templateName: finalTemplate.name,
            suggestedSigners: Array.isArray(item.suggestedSigners) 
              ? item.suggestedSigners.map((signer: any) => ({
                  name: signer.name || 'Unknown Signer',
                  email: signer.email || '',
                  role: signer.role || 'Signer'
                }))
              : [],
            documentContent: item.documentContent || '',
            why_relevant: item.why_relevant || 'Relevant to your search criteria'
          };
          return suggestion;
        }).filter((item): item is NewAgreementSuggestion => item !== null);
        
        return suggestions.length > 0 ? suggestions : null;
      }

      return null;
    } catch (error) {
      console.error('Error generating new agreement suggestions:', error);
      return null;
    }
  },

  /**
   * Generate audit template suggestions based on user query
   */
  async generateAuditTemplateSuggestions(
    searchQuery: string,
    existingAuditTemplateNames: string[],
    availableComplianceTemplates: ComplianceTemplate[]
  ): Promise<NewAuditTemplateSuggestion[] | null> {
    if (!USE_VERTEX_AI) {
      console.log('⚠️ Vertex AI not enabled for audit template suggestions');
      return null;
    }
    
    console.log('🔍 Generating audit template suggestions...');

    try {

      // Create a summary of available compliance templates for context
      const complianceTemplateSummary = availableComplianceTemplates
        .slice(0, 50) // Limit to avoid token limits
        .map(t => ({
          name: t.name,
          category: t.category,
          jurisdiction: t.jurisdiction
        }));

      const prompt = `You are an expert in compliance audits and regulatory frameworks. A user is searching for audit templates related to: "${searchQuery}"

IMPORTANT: The following audit templates ALREADY EXIST in the system. DO NOT suggest these or variations of these:
${existingAuditTemplateNames.map(name => `- ${name}`).join('\n')}

Based on your knowledge of audit frameworks (SOC2, ISO 27001, HIPAA, PCI-DSS, GDPR, NIST, etc.), suggest 2-3 COMPLETELY NEW and DIFFERENT audit templates that would be relevant to this search.

For each audit template, provide:
- A clear, descriptive name (e.g., "SOC2 Type II Audit", "ISO 27001:2022 Compliance Audit")
- A brief description of what this audit covers
- The audit type (SOC2, ISO27001, HIPAA, PCI-DSS, GDPR, NIST, Custom, etc.)
- Typical frequency (yearly, semi-yearly, or quarterly)
- Suggested compliance template names that should be associated with this audit (ONLY from the available compliance templates list below - use exact names)
- Missing compliance templates: For compliance requirements that are needed for this audit but are NOT in the available templates list, provide full details so they can be created
- Why this audit is relevant to the search query

Available compliance templates in the system (you can reference these by name in complianceTemplateNames):
${complianceTemplateSummary.map(t => `- ${t.name} (${t.category}, ${t.jurisdiction})`).join('\n')}

IMPORTANT: 
- complianceTemplateNames should ONLY include templates from the available list above (use exact names)
- missingComplianceTemplates should include compliance templates that are REQUIRED for this audit but are NOT in the available list above
- For missing compliance templates, provide complete details so they can be created automatically

Common audit types and their typical requirements:
- SOC2: Access controls, system operations, change management, risk assessment, monitoring
- ISO 27001: Information security management, risk assessment, security controls
- HIPAA: Patient data protection, access controls, breach notification, privacy rules
- PCI-DSS: Payment card data security, encryption, access controls, network security
- GDPR: Data protection, privacy rights, consent management, data breach notification

Provide your response as JSON array:
[
  {
    "name": "Audit Template Name",
    "description": "Brief description of what this audit covers",
    "auditType": "SOC2",
    "frequency": "yearly",
    "complianceTemplateNames": ["Access Control Review", "System Monitoring"],
    "missingComplianceTemplates": [
      {
        "name": "Change Management Review",
        "category": "Security",
        "frequency": "P3M",
        "owner_role": "IT Manager",
        "evidence_types": [
          {"type": "pdf", "label": "Change Request Form", "required": true},
          {"type": "pdf", "label": "Approval Documentation", "required": true}
        ],
        "pass_fail_criteria": "All system changes must be documented, approved, and tested before deployment",
        "jurisdiction": "SOC2",
        "jurisdiction_notes": "SOC2 CC8.1 requires formal change management processes",
        "risk_level": "High",
        "lead_times_days": [30, 60, 90],
        "description": "Quarterly review of change management processes",
        "why_needed": "Required for SOC2 CC8.1 compliance - change management controls"
      }
    ],
    "why_relevant": "Explanation of why this matches the user's search",
    "metadata": {
      "industry": "Technology",
      "jurisdiction": "US"
    }
  }
]

For missingComplianceTemplates, provide complete compliance template details including:
- name, category, frequency (ISO 8601 format: P1Y, P6M, P3M, etc.)
- owner_role, evidence_types, pass_fail_criteria
- jurisdiction, jurisdiction_notes, risk_level (High/Medium/Low)
- lead_times_days (array of days: [30, 60, 90])
- description, why_needed (explanation of why this is needed for the audit)

Focus on real, actual audit frameworks and standards. Be specific and accurate.`;

      console.log('[AI Search Service] ✅ Using Vertex AI for audit template suggestions');
      const parsed = await callVertexAIProxy(prompt, undefined, true) as any;
      
      // Validate the structure
      if (Array.isArray(parsed)) {
        const suggestions = parsed.map((item: any) => ({
          name: item.name || 'Untitled Audit Template',
          description: item.description || '',
          auditType: item.auditType || 'Custom',
          frequency: item.frequency || 'yearly',
          complianceTemplateNames: Array.isArray(item.complianceTemplateNames) 
            ? item.complianceTemplateNames 
            : [],
          missingComplianceTemplates: Array.isArray(item.missingComplianceTemplates)
            ? item.missingComplianceTemplates.map((missing: any) => ({
                name: missing.name || 'Untitled Compliance Template',
                category: missing.category || 'General',
                frequency: missing.frequency || 'P1Y',
                owner_role: missing.owner_role || 'Site Manager',
                evidence_types: Array.isArray(missing.evidence_types) ? missing.evidence_types : [],
                pass_fail_criteria: missing.pass_fail_criteria || '',
                jurisdiction: missing.jurisdiction || '',
                jurisdiction_notes: missing.jurisdiction_notes || '',
                risk_level: (missing.risk_level || 'Medium') as 'High' | 'Medium' | 'Low',
                lead_times_days: Array.isArray(missing.lead_times_days) ? missing.lead_times_days : [30, 60, 90],
                description: missing.description || '',
                why_needed: missing.why_needed || 'Required for this audit'
              }))
            : [],
          why_relevant: item.why_relevant || 'Relevant to your search criteria',
          metadata: item.metadata || {}
        })) as NewAuditTemplateSuggestion[];

        // Filter out suggestions that match existing audit template names
        const filtered = suggestions.filter(suggestion => {
          const suggestionNameLower = suggestion.name.toLowerCase();
          return !existingAuditTemplateNames.some(existingName => {
            const existingNameLower = existingName.toLowerCase();
            return suggestionNameLower === existingNameLower ||
                   suggestionNameLower.includes(existingNameLower) ||
                   existingNameLower.includes(suggestionNameLower);
          });
        });

        return filtered.length > 0 ? filtered : null;
      }

      return null;
    } catch (error) {
      console.error('Error generating audit template suggestions:', error);
      return null;
    }
  }
};

